
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';

export const metadata = {
  toc: true,
  maxTocLevel: 4,
  layout: 'guide',
  section: 'Transfer',
  title: 'Receiving Funds Using Transfer UI',
  description: 'Facilitate transfers with an intuitive user interface'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const Image = makeShortcode("Image");
const YouTubeEmbed = makeShortcode("YouTubeEmbed");
const MDXCodeBlock = makeShortcode("MDXCodeBlock");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Header title={metadata.title} subtitle={metadata.description} mdxType="Header" />
    <p>{`Plaid Transfer UI is a drop-in user interface that makes it easy for end users to authorize one-time transfers. Transfer UI is compliant with Nacha WEB guidelines and automatically captures and manages `}<a parentName="p" {...{
        "href": "/docs/transfer/creating-transfers/#managing-proof-of-authorization"
      }}>{`Proof of Authorization`}</a>{` on your behalf. `}</p>
    <p>{`If your use case involves one-time debit transfers and you do not have the resources to build your own authorization UX, Transfer UI may be a good fit for you. `}</p>
    <p>{`With Transfer UI, users are able to authorize payments or disbursements. (While Proof of Authorization requirements are not mandatory for credits, Transfer UI supports both credit and debit transfers.) Before authorizing a transfer, users are able to review transfer details, such as amount, fund origination account, fund target account, and more.`}</p>
    <Callout mdxType="Callout">
      <p>{`Transfer UI does not currently support `}<a parentName="p" {...{
          "href": "/docs/transfer/recurring-transfers/"
        }}>{`recurring transfers`}</a>{`, `}<a parentName="p" {...{
          "href": "/docs/auth/coverage/database-auth/"
        }}>{`Database Auth`}</a>{`, or `}<a parentName="p" {...{
          "href": "/docs/transfer/platform-payments/"
        }}>{`Transfer for Platforms`}</a>{`. Transfer UI cannot be used to capture standing authorizations such as those required for variable recurring payments.`}</p>
    </Callout>
    <Image src='/assets/img/docs/transfer/transfer_ui.png' alt="Mobile app transfer UI flow showing loan payment process, payment method selection, transfer confirmation, and success message." caption='Example Transfer UI flow.' expandable={true} mdxType="Image" />
    <YouTubeEmbed videoId='BlwZKTIpHJk' alignment='full' title='Plaid Transfer UI in 3 minutes' caption='Prefer to learn by watching? Get an overview of how Transfer UI works in just 3 minutes!' mdxType="YouTubeEmbed" />
    <h4 {...{
      "id": "integration"
    }}>{`Integration`}</h4>
    <p>{`To integrate with Transfer UI, follow these steps:`}</p>
    <ol>
      <li parentName="ol">
        <p parentName="li">{`In the Plaid Dashboard, create a `}<a parentName="p" {...{
            "href": "/docs/link/customization/"
          }}>{`Link customization`}</a>{` with "Account Select" set to the "Single select" selection.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Call `}<inlineCode parentName="p">{`/transfer/intent/create`}</inlineCode>{` to obtain a `}<inlineCode parentName="p">{`transfer_intent.id`}</inlineCode>{`.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Call `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{`, specifying `}<inlineCode parentName="p">{`["transfer"]`}</inlineCode>{` in the `}<inlineCode parentName="p">{`products`}</inlineCode>{` parameter, the `}<inlineCode parentName="p">{`transfer_intent.id`}</inlineCode>{` in the `}<inlineCode parentName="p">{`transfer.intent_id`}</inlineCode>{` parameter, and the name of your Link customization in the `}<inlineCode parentName="p">{`link_customization_name`}</inlineCode>{` parameter. If you already have an `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` for this user, you can provide it to `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` to streamline the Link flow, otherwise, the user will authenticate their bank account within the same Transfer UI session.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Initialize a Link instance using the `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` created in the previous step. For more details for your specific platform, see the `}<a parentName="p" {...{
            "href": "/docs/link"
          }}>{`Link documentation`}</a>{`.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`The user will now go through the Link flow to perform their transfer. The `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{` callback will indicate they have completed the Link flow.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`(Optional) You will receive a `}<inlineCode parentName="p">{`public_token`}</inlineCode>{` from the `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{` callback. If you do not already have an access token for this user's account, call `}<inlineCode parentName="p">{`/item/public_token/exchange`}</inlineCode>{` to exchange this public token for an `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` in order to streamline future transfers for this user.`}</p>
      </li>
    </ol>
    <h4 {...{
      "id": "transfer-ui-user-flows"
    }}>{`Transfer UI user flows`}</h4>
    <p>{`Depending on whether you provided an access token in the `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` step, the Transfer UI experience will differ.`}</p>
    <p>{`Bank on file flow (`}<inlineCode parentName="p">{`access_token`}</inlineCode>{` provided):`}</p>
    <Image src='/assets/img/docs/transfer/bank_on_file_user.png' alt="Plaid transfer UI flow: 1. Sending credentials. 2. Confirm transfer from Bank to customer. 3. Transfer success." caption='Example Transfer UI flow if the access_token is provided.' expandable={true} mdxType="Image" />
    <p>{`Net new user flow (`}<inlineCode parentName="p">{`access_token`}</inlineCode>{` not provided):`}</p>
    <Image src='/assets/img/docs/transfer/net_new_user.png' alt="6-step UI flow for Plaid transfer without access_token: connecting account, selecting bank, entering credentials, account selection, transfer confirmation, success." caption='Example Transfer UI flow if the access_token is not provided.' mdxType="Image" />
    <h4 {...{
      "id": "tracking-transfer-creation"
    }}>{`Tracking transfer creation`}</h4>
    <Callout mdxType="Callout">
      <p>{`The instructions in this section correspond to the Web and Webview libraries for Link. If you're using a mobile SDK, information about the transfer intent status can be found in the `}<inlineCode parentName="p">{`metadataJson`}</inlineCode>{` field in the SDK's `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{` callback. As an example, see `}<a parentName="p" {...{
          "href": "/docs/link/android/#link-android-onsuccess-metadata-metadataJson"
        }}>{`Android: metadataJson`}</a>{`.`}</p>
    </Callout>
    <p>{`You can determine whether a transfer was successfully created by referring to the `}<inlineCode parentName="p">{`transfer_status`}</inlineCode>{` field in the `}<inlineCode parentName="p">{`metadata`}</inlineCode>{` object returned by `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{`. A value of `}<inlineCode parentName="p">{`COMPLETE`}</inlineCode>{` indicates that the transfer was successfully created. A value of `}<inlineCode parentName="p">{`INCOMPLETE`}</inlineCode>{` indicates that the transfer was not created. Note that this field only indicates the status of a transfer creation. It does not indicate the status of a transfer (i.e., funds movement). For more information on transfer intents, see `}<a parentName="p" {...{
        "href": "#retrieving-additional-information-about-transfer-intents"
      }}>{`Retrieving additional information about transfer intents`}</a>{`. For help troubleshooting incomplete transfers, see `}<a parentName="p" {...{
        "href": "#troubleshooting-transfer-ui"
      }}>{`Troubleshooting Transfer UI`}</a>{`.`}</p>
    <p>{`When using Transfer UI, the `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{` callback is called at a different point in time in the Link flow. Typically, the `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{` callback is called after an account is successfully linked using Link. When using Transfer UI, however, the `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{` callback is called only after both of the following conditions are met: an account is successfully linked using Link and a transfer is confirmed via the UI. Even so, a successful `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{` callback does not by itself indicate that the transfer succeeded (i.e., funds movement). Check the `}<inlineCode parentName="p">{`transfer_status`}</inlineCode>{` field in the callback metadata for transfer creation status, and use the Transfer event APIs to confirm settlement.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "switcher=false #Example onSuccess metadata object, incomplete transfer creation",
        "switcher": "false",
        "#Example": true,
        "onSuccess": true,
        "metadata": true,
        "object,": true,
        "incomplete": true,
        "transfer": true,
        "creation": true
      }}>{`{
  institution: {
    name: 'Wells Fargo',
    institution_id: 'ins_4'
  },
  accounts: [
    {
      id: 'ygPnJweommTWNr9doD6ZfGR6GGVQy7fyREmWy',
      name: 'Plaid Checking',
      mask: '0000',
      type: 'depository',
      subtype: 'checking',
      verification_status: ''
    },
    {
      id: '9ebEyJAl33FRrZNLBG8ECxD9xxpwWnuRNZ1V4',
      name: 'Plaid Saving',
      mask: '1111',
      type: 'depository',
      subtype: 'savings'
    }
    ...
  ],
  transfer_status: 'INCOMPLETE',
  link_session_id: '79e772be-547d-4c9c-8b76-4ac4ed4c441a'
}
`}</code></pre>
    <h4 {...{
      "id": "retrieving-additional-information-about-transfer-intents"
    }}>{`Retrieving additional information about transfer intents`}</h4>
    <p>{`To retrieve more information about a transfer intent, call the `}<inlineCode parentName="p">{`/transfer/intent/get`}</inlineCode>{` endpoint and pass the `}<inlineCode parentName="p">{`transfer_intent_id`}</inlineCode>{` (returned by the `}<inlineCode parentName="p">{`/transfer/intent/create`}</inlineCode>{` endpoint).`}</p>
    <MDXCodeBlock type='server' title='Retrieving more information about a transfer intent' mdxType="MDXCodeBlock">
      <pre><code parentName="pre" {...{
          "className": "language-node"
        }}>{`const request: TransferIntentGetRequest = {
  transfer_intent_id: '460cbe92-2dcc-8eae-5ad6-b37d0ec90fd9',
};

try {
  const response = await client.transferIntentGet(request);
} catch (error) {
  // handle error
}
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-bash"
        }}>{`curl -X POST https://sandbox.plaid.com/transfer/intent/get \\
 -H 'Content-Type: application/json' \\
 -d '{
   "transfer_intent_id": "460cbe92-2dcc-8eae-5ad6-b37d0ec90fd9"
 }'
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-ruby"
        }}>{`request = Plaid::TransferIntentGetRequest.new(
  {
    transfer_intent_id: "460cbe92-2dcc-8eae-5ad6-b37d0ec90fd9",
  }
)
response = client.transfer_intent_get(request)
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-java"
        }}>{`TransferIntentGetRequest request = new TransferIntentGetRequest()
  .transferIntentId("460cbe92-2dcc-8eae-5ad6-b37d0ec90fd9");

Response<TransferIntentGetResponse> response = client()
  .transferIntentGet(request)
  .execute();
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-python"
        }}>{`request = TransferIntentGetRequest(
    transfer_intent_id='460cbe92-2dcc-8eae-5ad6-b37d0ec90fd9'
)
response = client.transfer_intent_get(request)
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go"
        }}>{`request := plaid.NewTransferIntentGetRequest(
  "TRANSFER_INTENT_ID",
)

response, _, err := client.PlaidApi.TransferIntentGet(ctx).TransferIntentGetRequest(*request).Execute()
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go",
          "metastring": "sdk=fern",
          "sdk": "fern"
        }}>{`import (
    transfer "github.com/plaid/plaid-go/v49/transfer"
)

request := &transfer.TransferIntentGetRequest{TransferIntentID: "TRANSFER_INTENT_ID"}

response, err := client.Transfer.Intent.Get(ctx, request)
`}</code></pre>
    </MDXCodeBlock>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "switcher=false #Example /transfer/intent/get response",
        "switcher": "false",
        "#Example": true,
        "/transfer/intent/get": true,
        "response": true
      }}>{`{
  "transfer_intent": {
    "account_id": "3gE5gnRzNyfXpBK5wEEKcymJ5albGVUqg77gr",
    "ach_class": "ppd",
    "amount": "15.75",
    "authorization_decision": "APPROVED",
    "authorization_decision_rationale": null,
    "created": "2020-08-06T17:27:15Z",
    "description": "Desc",
    "failure_reason": null,
    "id": "460cbe92-2dcc-8eae-5ad6-b37d0ec90fd9",
    "metadata": {
      "key1": "value1",
      "key2": "value2"
    },
    "mode": "DISBURSEMENT",
    "funding_account_id": "9853defc-e703-463d-86b1-dc0607a45359",
    "status": "SUCCEEDED",
    "transfer_id": "8945fedc-e703-463d-86b1-dc0607b55460",
    "user": {
      "address": {
        "street": "100 Market Street",
        "city": "San Francisco",
        "region": "California",
        "postal_code": "94103",
        "country": "US"
      },
      "email_address": "lknope@email.com",
      "legal_name": "Leslie Knope",
      "phone_number": "123-456-7890"
    }
  },
  "request_id": "saKrIBuEB9qJZno"
}
`}</code></pre>
    <p>{`The response is a `}<inlineCode parentName="p">{`transfer_intent`}</inlineCode>{` object with more information about the transfer intent.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`status`}</inlineCode>{` field in the response indicates whether the transfer intent was successfully captured. It can have one of the following values: `}<inlineCode parentName="p">{`FAILED`}</inlineCode>{`, `}<inlineCode parentName="p">{`SUCCEEDED`}</inlineCode>{`, or `}<inlineCode parentName="p">{`PENDING`}</inlineCode>{`.`}</p>
    <p>{`If the value of `}<inlineCode parentName="p">{`status`}</inlineCode>{` is `}<inlineCode parentName="p">{`FAILED`}</inlineCode>{`, the transfer intent was not captured. The `}<inlineCode parentName="p">{`transfer_id`}</inlineCode>{` field will be `}<inlineCode parentName="p">{`null`}</inlineCode>{` and the `}<inlineCode parentName="p">{`failure_reason`}</inlineCode>{` object will contain information about why the transfer intent failed. Some possible reasons may include: the authorization was declined, the account is blocked, or the origination account was invalid.`}</p>
    <p>{`If the value of `}<inlineCode parentName="p">{`status`}</inlineCode>{` is `}<inlineCode parentName="p">{`SUCCEEDED`}</inlineCode>{`, the transfer intent was successfully captured. The accompanying `}<inlineCode parentName="p">{`transfer_id`}</inlineCode>{` field in the response will be set to the ID of the originated transfer. This ID can be used with other `}<a parentName="p" {...{
        "href": "/docs/api/products/transfer/"
      }}>{`Transfer API endpoints`}</a>{`.`}</p>
    <p>{`A value of `}<inlineCode parentName="p">{`PENDING`}</inlineCode>{` can mean one of the following:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`The transfer intent has not yet been processed by the authorization engine (`}<inlineCode parentName="p">{`authorization_decision`}</inlineCode>{` is `}<inlineCode parentName="p">{`null`}</inlineCode>{`). This is the initial state of all transfer intents.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`The transfer intent was processed and approved by the authorization engine (`}<inlineCode parentName="p">{`authorization_decision`}</inlineCode>{` is `}<inlineCode parentName="p">{`"APPROVED"`}</inlineCode>{`), but has not yet been processed by the transfer creation processor.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`The transfer intent was processed by the authorization engine and was declined (`}<inlineCode parentName="p">{`authorization_decision`}</inlineCode>{` is `}<inlineCode parentName="p">{`"DECLINED"`}</inlineCode>{`) due to insufficient funds (`}<inlineCode parentName="p">{`authorization_decision_rationale.code`}</inlineCode>{` is `}<inlineCode parentName="p">{`"NSF"`}</inlineCode>{`). If this is the case, the end user can retry the transfer intent up to three times. The transfer intent status will remain as `}<inlineCode parentName="p">{`"PENDING"`}</inlineCode>{` throughout the retry intents. After three unsuccessful retries, the transfer intent status will be `}<inlineCode parentName="p">{`"FAILED"`}</inlineCode>{`.`}</p>
      </li>
    </ul>
    <h4 {...{
      "id": "transfer-ui-best-practices"
    }}>{`Transfer UI best practices`}</h4>
    <p>{`While Transfer UI provides a Nacha-compliant UI for your transfers, to maximize conversion, it is recommended to still provide context in your own app to your end users about the transfer. We recommend that your app implement a UI where the customer selects or confirms the transfer amount and, if applicable, the funds source or destination. To maximize user confidence, your app should also provide a post-Link success pane confirming the transfer details, including amount and effective date.`}</p>
    <h4 {...{
      "id": "sample-implementation"
    }}>{`Sample implementation`}</h4>
    <p>{`For a real-life example of an app that incorporates both Transfer and Transfer UI, see the Node-based `}<a parentName="p" {...{
        "href": "https://github.com/plaid/transfer-quickstart"
      }}>{`Transfer Quickstart`}</a>{` sample app. The Transfer Quickstart is a sample bill-pay app that enables ACH bank transfers.`}</p>
    <h4 {...{
      "id": "troubleshooting-transfer-ui"
    }}>{`Troubleshooting Transfer UI`}</h4>
    <h5 {...{
      "id": "the-onexit-callback-is-called"
    }}>{`The onExit callback is called`}</h5>
    <p>{`If the `}<inlineCode parentName="p">{`onExit`}</inlineCode>{` callback is called, the Link session failed to link an account. The transfer intent could not be initiated.`}</p>
    <h5 {...{
      "id": "onsuccess-is-called-but-the-transfer-intent-is-incomplete"
    }}>{`onSuccess is called, but the transfer intent is incomplete`}</h5>
    <p>{`The `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{` callback is not an indication of a successful transfer creation or a successful transfer (i.e., funds movement). To fully diagnose incomplete transfer intents (i.e., when the `}<inlineCode parentName="p">{`transfer_status`}</inlineCode>{` field in the `}<inlineCode parentName="p">{`metadata`}</inlineCode>{` object returned by `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{` is `}<inlineCode parentName="p">{`INCOMPLETE`}</inlineCode>{`), call the `}<inlineCode parentName="p">{`/transfer/intent/get`}</inlineCode>{` endpoint and pass in the corresponding transfer intent ID as an argument. Information about the transfer intent can be found in the `}<inlineCode parentName="p">{`status`}</inlineCode>{`, `}<inlineCode parentName="p">{`failure_reason`}</inlineCode>{`, `}<inlineCode parentName="p">{`authorization_decision`}</inlineCode>{`, and `}<inlineCode parentName="p">{`authorization_decision_rationale`}</inlineCode>{` fields in the response.`}</p>
    <h5 {...{
      "id": "network-errors-or-institution-connection-issues"
    }}>{`Network errors or institution connection issues`}</h5>
    <p>{`Network errors or institution connection issues can also result in incomplete transfer intents. In such cases, generate a new Link token using the existing `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` and invoke Transfer UI to allow the end user to attempt the transfer intent again.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "integration",
        level: 4,
        title: "Integration",
      },
{
        id: "transfer-ui-user-flows",
        level: 4,
        title: "Transfer UI user flows",
      },
{
        id: "tracking-transfer-creation",
        level: 4,
        title: "Tracking transfer creation",
      },
{
        id: "retrieving-additional-information-about-transfer-intents",
        level: 4,
        title: "Retrieving additional information about transfer intents",
      },
{
        id: "transfer-ui-best-practices",
        level: 4,
        title: "Transfer UI best practices",
      },
{
        id: "sample-implementation",
        level: 4,
        title: "Sample implementation",
      },
{
        id: "troubleshooting-transfer-ui",
        level: 4,
        title: "Troubleshooting Transfer UI",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
